import { IconNames, IconSizes, PresetRange } from '@dixa/lib-shared-habla';
import type {
  SearchFilterCondition,
  SearchFilterField,
  SearchFilterOperator,
  SearchFilters,
  SearchFilterStrategy,
  SearchFilterValueDatePresetKey,
} from 'lib-search-types';
import moment from 'moment-timezone';

import { FeatureFlagName } from 'utils/featureFlagging/featureFlags';
import { userTypeAssets } from 'utils/user';

import type { SearchViewScope } from '../../../search.types';

/** Represents the item type passed to dropdowns. */
export type FilterItemType = {
  id: number | string;
  name: string;
  color?: string | null;
  icon?: IconNames;
  iconSize?: IconSizes;
};

/** A map of static filter fields and items available to select in the dropdowns. */
export const filterItems: Record<
  'initial_direction' | 'status' | 'user_type',
  FilterItemType[]
> = {
  initial_direction: [
    { id: 'inbound', name: 'Inbound' },
    { id: 'outbound', name: 'Outbound' },
    { id: 'internal', name: 'Internal' },
  ],
  status: [
    { id: 'closed', name: 'Closed' },
    { id: 'pending', name: 'Follow-up' },
    { id: 'open', name: 'Open' },
  ],
  user_type: Object.entries(userTypeAssets)
    .filter(([id]) => id !== 'bot')
    .map(([id, { icon, prettyName }]) => ({
      id,
      name: prettyName,
      icon,
    })),
};

/** A moment object for today's date, starting at 12 AM */
const todayStart = moment().startOf('day');
/** A moment object for today's date, ending at 23:59 PM */
const todayEnd = moment().endOf('day');

/** A list of date range presets used for quick date selections. */
export const dateRangePresets: PresetRange[] = [
  {
    key: 'today',
    label: 'Today',
    startDate: todayStart,
    endDate: todayEnd,
  },
  {
    key: 'yesterday',
    label: 'Yesterday',
    startDate: todayStart.clone().subtract(1, 'day'),
    endDate: todayEnd.clone().subtract(1, 'day'),
  },
  {
    key: 'last_7_days',
    label: 'Last 7 days',
    startDate: todayStart.clone().subtract(7, 'day'),
    endDate: todayEnd,
  },
  {
    key: 'last_4_weeks',
    label: 'Last 4 weeks',
    startDate: todayStart.clone().subtract(4, 'week'),
    endDate: todayEnd,
  },
  {
    key: 'last_3_months',
    label: 'Last 3 months',
    startDate: todayStart.clone().subtract(3, 'month'),
    endDate: todayEnd,
  },
] satisfies Array<PresetRange & { key: SearchFilterValueDatePresetKey }>;

/** A list of date-time filter fields. */
export const dateFilterFields = [
  'created_at',
  'last_message_created_at',
  'updated_at',
  'assigned_at',
  'closed_at',
  'abandoned_at',
  'note_created_at',
] as const;

/**
 * A list of filter fields whose labels are defined or generated on the
 * client-side.
 */
export const staticFilterFields = [
  ...dateFilterFields,
  'initial_direction',
  'initial_channel',
  'status',
  'duration',
];

/** A map of search page scopes and the filter fields they support for filtering. */
export const filterFieldsPerTab: Record<
  SearchViewScope,
  SearchFilterField<'conversations'>[]
> = {
  conversations: [
    'tags',
    'queue_id',
    'agent_id',
    'requested_by',
    'status',
    'initial_channel',
    'initial_direction',
    'duration',
    'note_message',
    'note_author_id',
    'message_author_id',
    'numeric_id',
    ...dateFilterFields,
  ],
  users: ['company_id', 'user_type'],
  companies: ['company_id'],
};

/** Represents information about a filter field rendered in the UI. */
export type FilterFieldAssets = {
  /** The display label of the filter. */
  label: string;
  /** The description of the filter. */
  description: string;
  /** The icon of the filter. */
  icon: IconNames;
  /** An optional feature flag required to enable the filter. */
  featureFlag?: FeatureFlagName;
};

/** A map of filter fields and various display attributes. */
export const filterFieldAssets: Record<
  SearchFilterField<'conversations'>,
  FilterFieldAssets
> = {
  /** Conversation fields. */
  numeric_id: {
    label: 'ID',
    description: 'The id of the conversation.',
    icon: 'code',
  },
  status: {
    label: 'Status',
    description: 'Whether the conversation is open, closed or in follow up.',
    icon: 'lock',
  },
  initial_channel: {
    label: 'Channel',
    description: 'The channel the conversation originated from.',
    icon: 'channels',
  },
  initial_direction: {
    label: 'Direction',
    description: 'Whether this is an inbound or outbound conversation.',
    icon: 'exchange',
  },
  queue_id: {
    label: 'Queue',
    description: 'The current queue of the conversation.',
    icon: 'users',
  },
  agent_id: {
    label: 'Agent',
    description: 'The agent currently assigned to the conversation.',
    icon: 'headset',
  },
  requested_by: {
    label: 'Contact',
    description: 'The customer contact of the conversation.',
    icon: 'id-card',
  },
  tags: {
    label: 'Tag',
    description: 'The tags that are currently applied to the conversation.',
    icon: 'tag',
  },
  duration: {
    label: 'Duration',
    description:
      'The period from when the conversation was created until it was closed the last time, or time of search if the conversation is open.',
    icon: 'clock',
  },
  note_message: {
    label: 'Internal note',
    description: 'The content of the internal note in the conversation.',
    icon: 'sticky-note',
  },
  note_created_at: {
    label: 'Internal note created date',
    description: 'The date when the internal note was created.',
    icon: 'calendar-plus',
  },
  note_author_id: {
    label: 'Internal note author',
    description: 'The agent who wrote the internal note in the conversation.',
    icon: 'headset',
  },
  message_author_id: {
    label: 'Message author',
    description: 'The agent who wrote the message in the conversation.',
    icon: 'headset',
  },
  created_at: {
    label: 'Creation date',
    description: 'The date when the conversation was created.',
    icon: 'calendar-plus',
  },
  updated_at: {
    label: 'Last activity',
    description:
      'The date when the last activity occurred on the conversation. Conversation activity is defined as one of the following events: internal note added, new message added, tag added, transferred or assigned to a queue or an agent.',
    icon: 'calendar',
  },
  last_message_created_at: {
    label: 'Last message',
    description:
      'The date when the last message was added to the conversation.',
    icon: 'calendar-plus',
  },
  assigned_at: {
    label: 'Assigned date',
    description: 'The latest time the conversation was assigned to an agent.',
    icon: 'calendar',
  },
  closed_at: {
    label: 'Closed date',
    description: 'The latest time the conversation was closed.',
    icon: 'calendar-times',
  },
  abandoned_at: {
    label: 'Abandoned date',
    description: 'The date when the conversation was abandoned.',
    icon: 'calendar-times',
  },
  conversation_custom_attribute: {
    label: 'Conversation custom attribute',
    description: 'A custom attribute of the conversation.',
    icon: 'input',
  },
  link_type: {
    label: 'Linked type',
    description: 'The type of the conversation link.',
    icon: 'link',
  },
  linked_to: {
    label: 'Linked to',
    description: 'The conversation this is linked to.',
    icon: 'link',
  },
  is_abandoned: {
    label: 'Abandoned',
    description: 'Whether the conversation was abandoned or not.',
    icon: 'phone-hang',
  },

  /** Contact fields. */
  company_id: {
    label: 'Company',
    description: 'The company of the contact.',
    icon: 'building',
  },
  user_type: {
    label: 'User category',
    description: 'The category of the user.',
    icon: 'user',
  },
  contact_custom_attribute: {
    label: 'Contact custom attribute',
    description: 'A custom attribute of the contact.',
    icon: 'input',
  },
};

/** A map of filter strategies and display attributes. */
export const filterStrategyAssets: Record<
  SearchFilterStrategy,
  { label: string; description: string }
> = {
  all: {
    label: 'ALL',
    description: 'If all of the criteria are met',
  },
  any: {
    label: 'ONE OR MORE',
    description: 'If one or more of the criteria are met',
  },
};

/** The display names of the filter operators. */
export const filterOperatorLabels: Record<
  SearchFilterOperator<'conversations'>,
  string
> = {
  is_one: 'is one of',
  is_not_one: 'is not one of',
  includes_one: 'includes one of',
  excludes_one: 'excludes one of',
  includes_all: 'includes all of',
  excludes_all: 'excludes all of',
  is_empty: 'is empty',
  is_not_empty: 'is not empty',
  between: 'between',
  since: 'since',
  until: 'until',
  gte: 'longer than or equal to',
  lte: 'shorter than or equal to',
  contains: 'contains',
  not_contains: 'does not contain',
  equals: 'equals',
  not_equals: 'does not equal',
};

/** A map of filter operators grouped by the field data type they can operate on. */
export const filterOperatorGroups = {
  oneToOne: ['is_one', 'is_not_one'] as const,

  oneToMany: [
    'includes_one',
    'excludes_one',
    'includes_all',
    'excludes_all',
  ] as const,

  nullish: ['is_empty', 'is_not_empty'] as const,
  number: ['between', 'gte', 'lte'] as const,
  text: ['contains', 'not_contains'] as const,
  date: ['between', 'since', 'until'] as const,
  boolean: ['equals', 'not_equals'] as const,
};

/** Represents a type used to map filter fields and operators. */
type FieldOperators = {
  readonly [TCondition in SearchFilterCondition<'conversations'> as TCondition['field']]: {
    defaultOperator: TCondition['operator'];
    operators: TCondition['operator'][];
  };
};

/**
 * A map of filter fields and the operators supported for filtering on those
 * fields.
 */
export const fieldOperators: FieldOperators = {
  /** Conversation fields. */
  numeric_id: {
    defaultOperator: 'is_one',
    operators: [...filterOperatorGroups.oneToOne],
  },
  status: {
    defaultOperator: 'is_one',
    operators: [...filterOperatorGroups.oneToOne],
  },
  initial_channel: {
    defaultOperator: 'is_one',
    operators: [...filterOperatorGroups.oneToOne],
  },
  initial_direction: {
    defaultOperator: 'is_one',
    operators: [...filterOperatorGroups.oneToOne],
  },
  requested_by: {
    defaultOperator: 'is_one',
    operators: [...filterOperatorGroups.oneToOne],
  },
  queue_id: {
    defaultOperator: 'is_one',
    operators: [
      ...filterOperatorGroups.oneToOne,
      ...filterOperatorGroups.nullish,
    ],
  },
  agent_id: {
    defaultOperator: 'is_one',
    operators: [
      ...filterOperatorGroups.oneToOne,
      ...filterOperatorGroups.nullish,
    ],
  },
  tags: {
    defaultOperator: 'includes_one',
    operators: [
      ...filterOperatorGroups.oneToMany,
      ...filterOperatorGroups.nullish,
    ],
  },
  duration: {
    defaultOperator: 'between',
    operators: [...filterOperatorGroups.number],
  },
  note_message: {
    defaultOperator: 'contains',
    operators: [...filterOperatorGroups.text],
  },
  note_author_id: {
    defaultOperator: 'is_one',
    operators: [...filterOperatorGroups.oneToOne],
  },
  message_author_id: {
    defaultOperator: 'is_one',
    operators: [...filterOperatorGroups.oneToOne],
  },
  conversation_custom_attribute: {
    defaultOperator: 'is_one',
    operators: [
      ...filterOperatorGroups.oneToOne,
      ...filterOperatorGroups.nullish,
    ],
  },
  link_type: {
    defaultOperator: 'is_one',
    operators: [...filterOperatorGroups.oneToOne],
  },
  linked_to: {
    defaultOperator: 'is_one',
    operators: [
      ...filterOperatorGroups.oneToOne,
      ...filterOperatorGroups.nullish,
    ],
  },
  is_abandoned: {
    defaultOperator: 'equals',
    operators: [...filterOperatorGroups.boolean],
  },

  /** Contact fields. */
  user_type: {
    defaultOperator: 'is_one',
    operators: [...filterOperatorGroups.oneToOne],
  },
  company_id: {
    defaultOperator: 'is_one',
    operators: [
      ...filterOperatorGroups.oneToOne,
      ...filterOperatorGroups.nullish,
    ],
  },
  contact_custom_attribute: {
    defaultOperator: 'is_one',
    operators: [
      ...filterOperatorGroups.oneToOne,
      ...filterOperatorGroups.nullish,
    ],
  },

  /** All date fields used should and will always use the same operators. */
  ...dateFilterFields.reduce(
    (out, field) => ({
      ...out,
      [field]: {
        defaultOperator: 'between',
        operators: [...filterOperatorGroups.date],
      },
    }),
    {} as Pick<FieldOperators, (typeof dateFilterFields)[number]>,
  ),
};

/** The initial search filters configuration state. */
export const filtersInitialState: SearchFilters<'conversations'> = {
  strategy: 'all',
  filters: [],
};

/** Error messages displayed when validating filter conditions. */
export const filterValidationErrors = {
  noValue: 'Please select at least one value or remove the filter',
  noDate: 'Please select a date or remove the filter',
  invalidRange: 'Please select both dates or use a different operator',
};
